import {
  companyAddressCountryFieldKey,
  companyAddressTextFieldKey,
} from 'shared/domain/company/mapping';
import { ProjectSetupTranslationKeys } from 'intl/keys';
import type { TranslationKey } from 'intl/types/translationKey';
import {
  CompanyAddressKind,
  companyAddressKinds,
  CompanyAddressTextPart,
  companyAddressTextParts,
} from 'shared/domain/company/types/model';
import type {
  CompanyAddressCountryFieldKey,
  CompanyAddressesOnView,
  CompanyAddressTextFieldKey,
} from 'shared/domain/company/types/view';
import { LabelledEntity } from 'shared/types/commonView';
import { deepEqual } from 'shared/utils/deepEqual';
import {
  CompanyAddressCopySource,
  CompanyAddressFieldsProps,
} from './types';

const addressTitleIds: Record<CompanyAddressKind, TranslationKey> = {
  correspondenceAddress:
    ProjectSetupTranslationKeys.correspondenceAddressFieldLabel,
  registeredOfficeAddress:
    ProjectSetupTranslationKeys.registeredOfficeAddress,
};

const addressTextPartLabelIds: Record<
  CompanyAddressTextPart,
  TranslationKey
> = {
  streetAndNumber: ProjectSetupTranslationKeys.addressStreetAndNumber,
  postalCode: ProjectSetupTranslationKeys.addressPostalCode,
  city: ProjectSetupTranslationKeys.addressCity,
};

const addressCopySources: Partial<
  Record<CompanyAddressKind, CompanyAddressCopySource>
> = {
  correspondenceAddress: {
    kind: 'registeredOfficeAddress',
    labelId: ProjectSetupTranslationKeys.sameAsRegisteredOfficeAddress,
  },
};

/** Shared by every address field: optional, as the API takes an address only complete (see validation). */
function addressFieldBase(
  formKey: CompanyAddressTextFieldKey | CompanyAddressCountryFieldKey,
  isDialog: boolean
) {
  return {
    formKey,
    required: false,
    localStorageKey: `company-${formKey}`,
    reserveSpaceForHelperText: !isDialog,
    dense: true,
    'data-qa': `company_${formKey}_field`,
    issueParams: null,
  };
}

/** Street, postal code and city as text, the country picked from the ISO list, for each company address. */
export function toAddressesFieldsProps(
  isDialog: boolean,
  countries: LabelledEntity[]
): CompanyAddressFieldsProps[] {
  return companyAddressKinds.map((kind) => {
    const countryFormKey = companyAddressCountryFieldKey(kind);
    return {
      kind,
      copySource: addressCopySources[kind],
      titleId: addressTitleIds[kind],
      textFieldsProps: companyAddressTextParts.map((part) => {
        const formKey = companyAddressTextFieldKey(kind, part);
        return {
          ...addressFieldBase(formKey, isDialog),
          labelId: addressTextPartLabelIds[part],
          fieldName: `text-field-${formKey}`,
        };
      }),
      countryFieldProps: {
        ...addressFieldBase(countryFormKey, isDialog),
        labelId: ProjectSetupTranslationKeys.addressCountry,
        fieldName: `singleselect-${countryFormKey}`,
        available: countries,
        getOptionLabel: (option: LabelledEntity): string => option.label,
      },
    };
  });
}

/** The form values that fill the `to` address with the parts of the `from` one. */
export function copiedAddressFields(
  fields: CompanyAddressesOnView,
  from: CompanyAddressKind,
  to: CompanyAddressKind
): [keyof CompanyAddressesOnView, unknown][] {
  return [
    ...companyAddressTextParts.map(
      (part): [keyof CompanyAddressesOnView, unknown] => [
        companyAddressTextFieldKey(to, part),
        fields[companyAddressTextFieldKey(from, part)],
      ]
    ),
    [
      companyAddressCountryFieldKey(to),
      fields[companyAddressCountryFieldKey(from)],
    ],
  ];
}

/**
 * Whether the `to` address already holds every part of the `from` one. Compared
 * by value, as the form may hold a new country option object.
 */
export function isAddressCopied(
  fields: CompanyAddressesOnView,
  from: CompanyAddressKind,
  to: CompanyAddressKind
): boolean {
  return copiedAddressFields(fields, from, to).every(([key, value]) =>
    deepEqual(fields[key], value)
  );
}
